import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CreditCard, Smartphone, Shield, Eye, EyeOff, Save, CheckCircle, AlertTriangle, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
};

const gatewayIcons = {
  stripe: CreditCard,
  mpesa: Smartphone,
  airtel_money: Smartphone,
  orange_money: Smartphone,
  maishapay: Smartphone
};

const gatewayColors = {
  stripe: 'from-indigo-500 to-purple-600',
  mpesa: 'from-green-500 to-emerald-600',
  airtel_money: 'from-red-500 to-rose-600',
  orange_money: 'from-orange-500 to-amber-600',
  maishapay: 'from-sky-500 to-blue-600'
};

const AdminPaymentConfigPage = () => {
  const { user, language } = useAuth();
  const [gateways, setGateways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [showSecrets, setShowSecrets] = useState({});
  const [editedConfigs, setEditedConfigs] = useState({});

  useEffect(() => {
    loadGateways();
  }, []);

  const loadGateways = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/payment-gateways`, { headers: getAuthHeaders() });
      const data = res.data;
      setGateways(data.gateways || []);
      const initial = {};
      (data.gateways || []).forEach(gw => {
        initial[gw.gateway_id] = {
          enabled: gw.enabled || false,
          test_mode: gw.test_mode !== false,
          config: { ...(gw.config || {}) }
        };
      });
      setEditedConfigs(initial);
    } catch (err) {
      toast.error('Erreur de chargement des passerelles');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (gatewayId) => {
    setSaving(prev => ({ ...prev, [gatewayId]: true }));
    try {
      const payload = editedConfigs[gatewayId];
      await axios.put(`${API}/api/admin/payment-gateways/${gatewayId}`, payload, { headers: getAuthHeaders() });
      toast.success(language === 'fr' ? 'Configuration sauvegardée' : 'Configuration saved');
      loadGateways();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setSaving(prev => ({ ...prev, [gatewayId]: false }));
    }
  };

  const updateConfig = (gatewayId, key, value) => {
    setEditedConfigs(prev => ({
      ...prev,
      [gatewayId]: {
        ...prev[gatewayId],
        config: {
          ...prev[gatewayId]?.config,
          [key]: value
        }
      }
    }));
  };

  const toggleEnabled = (gatewayId, val) => {
    setEditedConfigs(prev => ({
      ...prev,
      [gatewayId]: { ...prev[gatewayId], enabled: val }
    }));
  };

  const toggleTestMode = (gatewayId, val) => {
    setEditedConfigs(prev => ({
      ...prev,
      [gatewayId]: { ...prev[gatewayId], test_mode: val }
    }));
  };

  const toggleSecret = (gatewayId, fieldKey) => {
    const k = `${gatewayId}_${fieldKey}`;
    setShowSecrets(prev => ({ ...prev, [k]: !prev[k] }));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="payment-config-page">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
            <Settings2 className="w-8 h-8 text-primary" />
            {language === 'fr' ? 'Configuration des Paiements' : 'Payment Configuration'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {language === 'fr'
              ? 'Configurez les passerelles de paiement (Carte bancaire et Mobile Money)'
              : 'Configure payment gateways (Card and Mobile Money)'}
          </p>
        </div>

        <div className="grid gap-6">
          {gateways.map(gw => {
            const Icon = gatewayIcons[gw.gateway_id] || CreditCard;
            const colorClass = gatewayColors[gw.gateway_id] || 'from-gray-500 to-gray-600';
            const edited = editedConfigs[gw.gateway_id] || {};
            const isEnabled = edited.enabled || false;
            const isTestMode = edited.test_mode !== false;

            return (
              <Card key={gw.gateway_id} className={`relative overflow-hidden transition-all ${isEnabled ? 'ring-2 ring-primary/30' : 'opacity-80'}`} data-testid={`gateway-${gw.gateway_id}`}>
                <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${colorClass}`} />
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2.5 rounded-lg bg-gradient-to-br ${colorClass} text-white`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{gw.name}</CardTitle>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {gw.description || (gw.type === 'card' ? 'Carte bancaire' : gw.type === 'all_in_one' ? 'Mobile Money, Cartes, E-Wallets' : 'Mobile Money')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {isEnabled && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">
                            {language === 'fr' ? 'Mode test' : 'Test mode'}
                          </span>
                          <Switch
                            checked={isTestMode}
                            onCheckedChange={(val) => toggleTestMode(gw.gateway_id, val)}
                            data-testid={`toggle-test-${gw.gateway_id}`}
                          />
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">
                          {isEnabled
                            ? <span className="text-green-600 flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5" /> Activé</span>
                            : <span className="text-muted-foreground">Désactivé</span>
                          }
                        </span>
                        <Switch
                          checked={isEnabled}
                          onCheckedChange={(val) => toggleEnabled(gw.gateway_id, val)}
                          data-testid={`toggle-enable-${gw.gateway_id}`}
                        />
                      </div>
                    </div>
                  </div>
                  {isTestMode && isEnabled && (
                    <div className="flex items-center gap-2 mt-2 p-2 rounded-md bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 text-xs">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                      {language === 'fr'
                        ? 'Mode test activé : les paiements seront simulés sans transaction réelle'
                        : 'Test mode enabled: payments will be simulated without real transactions'}
                    </div>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  {(gw.fields || []).map(field => {
                    const secretKey = `${gw.gateway_id}_${field.key}`;
                    const isSecret = field.type === 'password';
                    const isVisible = showSecrets[secretKey];
                    const currentVal = edited.config?.[field.key] || '';

                    if (field.type === 'select') {
                      return (
                        <div key={field.key} className="space-y-1.5">
                          <Label className="text-sm">{field.label}</Label>
                          <Select
                            value={currentVal || field.options?.[0]}
                            onValueChange={(val) => updateConfig(gw.gateway_id, field.key, val)}
                          >
                            <SelectTrigger data-testid={`field-${gw.gateway_id}-${field.key}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {(field.options || []).map(opt => (
                                <SelectItem key={opt} value={opt}>
                                  {field.labels?.[opt] || opt}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      );
                    }

                    return (
                      <div key={field.key} className="space-y-1.5">
                        <Label className="text-sm flex items-center gap-1.5">
                          {field.label}
                          {field.required && <span className="text-red-500">*</span>}
                        </Label>
                        <div className="relative">
                          <Input
                            type={isSecret && !isVisible ? 'password' : 'text'}
                            value={currentVal}
                            onChange={(e) => updateConfig(gw.gateway_id, field.key, e.target.value)}
                            placeholder={field.placeholder || ''}
                            className={isSecret ? 'pr-10' : ''}
                            data-testid={`field-${gw.gateway_id}-${field.key}`}
                          />
                          {isSecret && (
                            <button
                              type="button"
                              onClick={() => toggleSecret(gw.gateway_id, field.key)}
                              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted transition-colors"
                            >
                              {isVisible ? <EyeOff className="w-4 h-4 text-muted-foreground" /> : <Eye className="w-4 h-4 text-muted-foreground" />}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  <div className="flex justify-end pt-3 border-t">
                    <Button
                      onClick={() => handleSave(gw.gateway_id)}
                      disabled={saving[gw.gateway_id]}
                      className="gap-2"
                      data-testid={`save-${gw.gateway_id}`}
                    >
                      <Save className="w-4 h-4" />
                      {saving[gw.gateway_id]
                        ? (language === 'fr' ? 'Sauvegarde...' : 'Saving...')
                        : (language === 'fr' ? 'Sauvegarder' : 'Save')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Card className="bg-muted/30">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-primary mt-0.5 shrink-0" />
              <div className="text-sm text-muted-foreground space-y-1">
                <p className="font-medium text-foreground">
                  {language === 'fr' ? 'Informations de sécurité' : 'Security Information'}
                </p>
                <p>
                  {language === 'fr'
                    ? 'Les clés API sont stockées de manière sécurisée dans la base de données. En mode test, les paiements Mobile Money sont simulés automatiquement.'
                    : 'API keys are stored securely in the database. In test mode, Mobile Money payments are simulated automatically.'}
                </p>
                <p>
                  {language === 'fr'
                    ? 'Pour passer en production, désactivez le mode test et assurez-vous que vos clés API sont valides.'
                    : 'To go live, disable test mode and ensure your API keys are valid.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdminPaymentConfigPage;
